import { z } from 'zod'
import type { MatchFilters } from '@/api/queries'

/**
 * Filters live in the URL, always.
 *
 * That makes every view shareable, the back button correct, and a bug report a
 * link. It also means the search params ARE the query cache key, so no separate
 * state container is needed — and cannot drift from what the address bar says.
 *
 * `.catch()` on each field means a hand-edited URL degrades to "no filter"
 * rather than crashing the route.
 */
/**
 * URL booleans need explicit parsing. `z.coerce.boolean()` runs `Boolean(value)`,
 * and `Boolean('false') === true` — so `?has_stats=false` silently filtered to
 * matches that DO have stats. Parse the two literals instead.
 */
const urlBool = z
  .union([z.boolean(), z.literal('true'), z.literal('false')])
  .transform((v) => v === true || v === 'true')
  .optional()
  .catch(undefined)

export const matchSearchSchema = z.object({
  season: z.coerce.number().int().min(2009).max(2024).optional().catch(undefined),
  surface: z.enum(['hard', 'clay', 'grass', 'carpet']).optional().catch(undefined),
  tour: z.enum(['ATP', 'WTA', 'CHALLENGER']).optional().catch(undefined),
  gender: z.enum(['M', 'F']).optional().catch(undefined),
  round_no: z.coerce.number().int().min(1).max(8).optional().catch(undefined),
  status: z.enum(['complete', 'retired', 'walkover']).optional().catch(undefined),
  indoor: urlBool,
  qualification: urlBool,
  has_stats: urlBool,
  tournament: z.string().max(60).optional().catch(undefined),
  player_id: z.coerce.number().int().positive().optional().catch(undefined),
  opponent_id: z.coerce.number().int().positive().optional().catch(undefined),
  date_from: z.string().optional().catch(undefined),
  date_to: z.string().optional().catch(undefined),
  match: z.string().optional().catch(undefined), // open detail panel

  // View state. In the URL for the same reason filters are: a colleague opening
  // the link sees the same table, not just the same rows.
  cols: z.string().max(400).optional().catch(undefined),
  sort: z.string().max(40).optional().catch(undefined),
  order: z.enum(['asc', 'desc']).optional().catch(undefined),

  // player attributes: "either player" normally, "the opponent" once a player
  // is selected (you already know your own player's height and hand)
  hand: z.enum(['R', 'L']).optional().catch(undefined),
  height_min: z.coerce.number().int().min(120).max(230).optional().catch(undefined),
  height_max: z.coerce.number().int().min(120).max(230).optional().catch(undefined),
  age_min: z.coerce.number().int().min(14).max(50).optional().catch(undefined),
  age_max: z.coerce.number().int().min(14).max(50).optional().catch(undefined),

  // venue
  venue_country: z.string().max(60).optional().catch(undefined),
  elevation_min: z.coerce.number().int().min(-500).max(6000).optional().catch(undefined),
  elevation_max: z.coerce.number().int().min(-500).max(6000).optional().catch(undefined),

  // per-match facets
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
  dow: z.coerce.number().int().min(0).max(6).optional().catch(undefined),
  hour_from: z.coerce.number().int().min(0).max(23).optional().catch(undefined),
  hour_to: z.coerce.number().int().min(0).max(23).optional().catch(undefined),
  min_tiebreaks: z.coerce.number().int().min(0).max(10).optional().catch(undefined),
  total_games_min: z.coerce.number().int().min(0).optional().catch(undefined),
  total_games_max: z.coerce.number().int().min(0).optional().catch(undefined),

  // per-PLAYER facets — only meaningful with player_id set (the API rejects
  // them otherwise rather than guessing "either player")
  hold_pct_min: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  break_pct_min: z.coerce.number().int().min(0).max(100).optional().catch(undefined),
  set1_won: urlBool,
  set2_won: urlBool,
  set3_won: urlBool,
  set4_won: urlBool,
  set5_won: urlBool,
  tb_won_min: z.coerce.number().int().min(0).max(5).optional().catch(undefined),
  is_favourite: urlBool,
  price_min: z.coerce.number().min(1.01).optional().catch(undefined),
  price_max: z.coerce.number().min(1.01).optional().catch(undefined),
})

/** Filters that describe one player rather than the match. Cleared whenever the
 *  player is cleared, so the URL can never hold a combination the API rejects. */
export const PLAYER_FACETS = [
  'hold_pct_min',
  'break_pct_min',
  'set1_won',
  'set2_won',
  'set3_won',
  'tb_won_min',
  'is_favourite',
  'price_min',
  'price_max',
] as const

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
export const DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export type MatchSearch = z.infer<typeof matchSearchSchema>

/** Strip params the filter endpoints do not take. `cols`/`sort`/`order` are
 *  view state and travel separately so they never re-key the count query. */
export function toApiFilters(s: MatchSearch): MatchFilters {
  const { match: _m, cols: _c, sort: _s, order: _o, ...rest } = s
  return rest
}

export const ROUND_LABELS: Record<number, string> = {
  1: 'Round of 128',
  2: 'Round of 64',
  3: 'Round of 32',
  4: 'Round of 16',
  5: 'Round of 8',
  6: 'Quarter-final',
  7: 'Semi-final',
  8: 'Final',
}

type ChipDef = { key: keyof MatchSearch; label: string }

export function activeChips(s: MatchSearch, playerName?: string): ChipDef[] {
  const out: ChipDef[] = []
  if (s.season) out.push({ key: 'season', label: String(s.season) })
  if (s.surface) out.push({ key: 'surface', label: s.surface })
  if (s.tour) out.push({ key: 'tour', label: s.tour })
  if (s.gender) out.push({ key: 'gender', label: s.gender === 'M' ? 'Men' : 'Women' })
  if (s.round_no)
    out.push({
      key: 'round_no',
      label: ROUND_LABELS[s.round_no] ?? `Round ${s.round_no}`,
    })
  if (s.status) out.push({ key: 'status', label: s.status })
  if (s.indoor !== undefined)
    out.push({ key: 'indoor', label: s.indoor ? 'Indoor' : 'Outdoor' })
  if (s.qualification !== undefined)
    out.push({
      key: 'qualification',
      label: s.qualification ? 'Qualifying' : 'Main draw',
    })
  if (s.has_stats) out.push({ key: 'has_stats', label: 'Has statistics' })
  if (s.tournament) out.push({ key: 'tournament', label: s.tournament })
  if (s.player_id)
    out.push({ key: 'player_id', label: playerName ?? `Player #${s.player_id}` })
  const who = playerName ? 'opp' : 'any'
  if (s.hand)
    out.push({ key: 'hand', label: `${who} ${s.hand === 'L' ? 'left' : 'right'}-handed` })
  if (s.height_min) out.push({ key: 'height_min', label: `${who} ≥${s.height_min}cm` })
  if (s.height_max) out.push({ key: 'height_max', label: `${who} ≤${s.height_max}cm` })
  if (s.age_min) out.push({ key: 'age_min', label: `${who} age ≥${s.age_min}` })
  if (s.age_max) out.push({ key: 'age_max', label: `${who} age ≤${s.age_max}` })
  if (s.venue_country) out.push({ key: 'venue_country', label: s.venue_country })
  if (s.elevation_min) out.push({ key: 'elevation_min', label: `≥${s.elevation_min}m` })
  if (s.elevation_max) out.push({ key: 'elevation_max', label: `≤${s.elevation_max}m` })
  if (s.month) out.push({ key: 'month', label: MONTHS[s.month - 1] ?? String(s.month) })
  if (s.dow !== undefined) out.push({ key: 'dow', label: DAYS[s.dow] ?? String(s.dow) })
  if (s.hour_from !== undefined && s.hour_to !== undefined)
    out.push({ key: 'hour_from', label: `${s.hour_from}:00–${s.hour_to}:00` })
  if (s.min_tiebreaks)
    out.push({ key: 'min_tiebreaks', label: `${s.min_tiebreaks}+ tiebreaks` })
  if (s.total_games_min)
    out.push({ key: 'total_games_min', label: `${s.total_games_min}+ games` })
  if (s.total_games_max)
    out.push({ key: 'total_games_max', label: `≤${s.total_games_max} games` })
  if (s.hold_pct_min) out.push({ key: 'hold_pct_min', label: `hold ≥${s.hold_pct_min}%` })
  if (s.break_pct_min)
    out.push({ key: 'break_pct_min', label: `break ≥${s.break_pct_min}%` })
  if (s.set1_won !== undefined)
    out.push({ key: 'set1_won', label: s.set1_won ? 'won set 1' : 'lost set 1' })
  if (s.set2_won !== undefined)
    out.push({ key: 'set2_won', label: s.set2_won ? 'won set 2' : 'lost set 2' })
  if (s.set3_won !== undefined)
    out.push({ key: 'set3_won', label: s.set3_won ? 'won set 3' : 'lost set 3' })
  if (s.tb_won_min)
    out.push({ key: 'tb_won_min', label: `${s.tb_won_min}+ tiebreaks won` })
  if (s.is_favourite !== undefined)
    out.push({ key: 'is_favourite', label: s.is_favourite ? 'favourite' : 'underdog' })
  if (s.price_min) out.push({ key: 'price_min', label: `price ≥${s.price_min}` })
  if (s.price_max) out.push({ key: 'price_max', label: `price ≤${s.price_max}` })
  if (s.date_from) out.push({ key: 'date_from', label: `from ${s.date_from}` })
  if (s.date_to) out.push({ key: 'date_to', label: `to ${s.date_to}` })
  return out
}

export function hasAnyFilter(s: MatchSearch) {
  return activeChips(s).length > 0
}

/** Hold and break come from match_stats, present for only ~34% of matches. */
export function usesStatsFilters(s: MatchSearch) {
  return s.hold_pct_min !== undefined || s.break_pct_min !== undefined
}
